import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import fdLogo from '../assets/images/logo-fisher-dugmore-financial.png';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Quill from 'quill';
import TableUI from 'quill-table-ui';
import 'quill-table-ui/dist/index.css';
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import Paper from "@mui/material/Paper";
import Icon from "@mui/material/Icon";
import { convertJsonToHtml } from "../utils/jsonToHtmlConverter";

Quill.register(
  {
    'modules/tableUI': TableUI
  },
  true
);

// Constants
const VARIABLE_GROUPS = [
  {
    label: "individuals",
    options: [
      "first name",
      "last name",
      "id number",
      "email",
      "phone",
      "address",
      "tax number",
      "date of birth",
    ],
  },
  {
    label: "entities",
    options: [
      "entity name",
      "organization legal name",
      "entity registration number",
      "license number",
      "address",
    ],
  },
  {
    label: "financial advisor",
    options: [
      "first name",
      "last name",
      "full name",
      "id number",
      "email",
      "branch",
    ],
  },
  {
    label: "input types",
    options: [
      "text",
      "number",
      "date",
      "select",
      "checkbox",
      "radio",
      "textarea",
      "email",
      "phone",
      "url",
    ],
  },
  {
    label: "signatures",
    options: [
      "client signature",
      "adviser signature",
    ],
  },
];

const GROUP_COLORS = {
  individuals: 'primary',
  entities: 'success',
  'financial advisor': 'warning',
  'input types': 'secondary',
  signatures: 'info',
};

// Default configuration
const DEFAULT_ZOOM_LEVEL = 1;
const MAX_ZOOM_LEVEL = 3;
const MIN_ZOOM_LEVEL = 0.5;
const ZOOM_INCREMENT = 0.25;

const defaultJsonTemplate = `{
  "pages": [
    {
      "sections": [
        {
          "text": "# Contract Agreement",
          "align": "center"
        },
        {
          "text": "This agreement is entered into between the parties listed herein.",
          "align": "left"
        },
        {
          "text": "## Party Information",
          "align": "left"
        },
        {
          "text": "**Client Name:** {{client_name}}",
          "align": "left"
        },
        {
          "text": "**Client ID:** {{client_id_number}}",
          "align": "left"
        },
        {
          "text": "## Terms and Conditions",
          "items": [
            {
              "text": "1. Payment Terms: {{ input_text_field }}"
            },
            {
              "text": "2. Service Description: {{ input_text_area }}"
            }
          ]
        },
        {
          "text": "## Signatures",
          "align": "center"
        },
        {
          "text": "By signing below, both parties agree to the terms and conditions outlined in this agreement.",
          "align": "center"
        },
        {
          "text": "{{ client_signature}} {{adviser_signature}}",
          "align": "center"
        }
      ],
      "orientation": "portrait"
    }
  ]
}`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["table"],
  ],
  tableUI: true,
};

/**
 * CreateTemplateBetaModal component for creating and editing document templates.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onSave - Function to save the template
 * @param {Object} [props.initialTemplate] - Initial template data for editing
 */
function CreateTemplateBetaModal({ isOpen, onClose, onSave, initialTemplate = null }) {
  const [templateName, setTemplateName] = useState("");
  const [content, setContent] = useState("");
  const [jsonInput, setJsonInput] = useState("");
  const [convertedHtml, setConvertedHtml] = useState("");
  const [conversionError, setConversionError] = useState(null);
  const quillRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);

  // Handle initial template data
  React.useEffect(() => {
    if (initialTemplate && isOpen) {
      setTemplateName(initialTemplate.name || "");
      
      // If template has JSON data, load it and convert
      if (initialTemplate.jsonString) {
        setJsonInput(initialTemplate.jsonString);
        
        // Auto-convert the JSON
        const result = convertJsonToHtml(initialTemplate.jsonString);
        if (result.success) {
          setConvertedHtml(result.html);
          setConversionError(null);
          
          // Convert HTML to rich text for the editor
          const richTextContent = convertHtmlToRichText(result.html);
          setContent(richTextContent);
          
          // Switch to PDF preview tab to show the converted HTML
          setActiveTab(1);
        } else {
          setConversionError(result.message);
          setConvertedHtml("");
        }
      }
    }
  }, [initialTemplate, isOpen]);

  const handleSave = () => {
    if (!templateName.trim()) return;
    onSave({ name: templateName.trim(), content });
    setTemplateName("");
    setContent("");
  };

  const handleClose = () => {
    setTemplateName("");
    setContent("");
    setJsonInput("");
    setConvertedHtml("");
    setConversionError(null);
    onClose();
  };

  const handleJsonConversion = () => {
    const result = convertJsonToHtml(jsonInput);
    if (result.success) {
      setConvertedHtml(result.html);
      setConversionError(null);
    } else {
      setConversionError(result.message);
      setConvertedHtml("");
    }
  };

  const handleUseHtmlInEditor = () => {
    if (convertedHtml) {
      // Convert HTML to rich text format for the editor
      const richTextContent = convertHtmlToRichText(convertedHtml);
      setContent(richTextContent);
      setActiveTab(0); // Switch to template content tab
    }
  };

  // Function to convert HTML to rich text format
  const convertHtmlToRichText = (html) => {
    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Convert the HTML to rich text format
    let richText = '';
    
    // Process each child node
    Array.from(tempDiv.children).forEach((element, index) => {
      const tagName = element.tagName.toLowerCase();
      
      switch (tagName) {
        case 'h1':
          richText += `<h1>${element.textContent}</h1>`;
          break;
        case 'h2':
          richText += `<h2>${element.textContent}</h2>`;
          break;
        case 'p':
          // Handle paragraphs with mixed content (text and form inputs)
          let paragraphContent = '';
          Array.from(element.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              paragraphContent += node.textContent;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const nodeTagName = node.tagName.toLowerCase();
              if (nodeTagName === 'input') {
                const inputType = node.getAttribute('type') || 'text';
                const inputName = node.getAttribute('name') || '';
                const placeholder = node.getAttribute('placeholder') || '';
                
                // Create more descriptive placeholders
                let placeholderText = '';
                if (inputName) {
                  placeholderText = inputName.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
                } else if (placeholder) {
                  placeholderText = placeholder;
                } else {
                  placeholderText = inputType;
                }
                
                richText += `{{${placeholderText.replace(/\s+/g, '_')}}}`;
              } else if (nodeTagName === 'select') {
                const selectName = node.getAttribute('name') || '';
                const placeholderText = selectName ? selectName.replace(/([A-Z])/g, ' $1').toLowerCase().trim() : 'select_option';
                richText += `{{${placeholderText.replace(/\s+/g, '_')}}}`;
              } else if (nodeTagName === 'textarea') {
                const textareaName = node.getAttribute('name') || '';
                const placeholderText = textareaName ? textareaName.replace(/([A-Z])/g, ' $1').toLowerCase().trim() : 'text_area';
                richText += `{{${placeholderText.replace(/\s+/g, '_')}}}`;
              } else {
                paragraphContent += node.textContent;
              }
            }
          });
          if (paragraphContent.trim()) {
            richText += `<p>${paragraphContent.trim()}</p>`;
          }
          break;
        case 'ol':
          richText += '<ol>';
          Array.from(element.querySelectorAll('li')).forEach(li => {
            richText += `<li>${li.textContent}</li>`;
          });
          richText += '</ol>';
          break;
        case 'ul':
          richText += '<ul>';
          Array.from(element.querySelectorAll('li')).forEach(li => {
            richText += `<li>${li.textContent}</li>`;
          });
          richText += '</ul>';
          break;
        case 'div':
          // Handle div elements (like table containers)
          if (element.classList.contains('table-container')) {
            const table = element.querySelector('table');
            if (table) {
              richText += '<table>';
              // Process table headers
              const thead = table.querySelector('thead');
              if (thead) {
                richText += '<thead><tr>';
                Array.from(thead.querySelectorAll('th')).forEach(th => {
                  richText += `<th>${th.textContent}</th>`;
                });
                richText += '</tr></thead>';
              }
              // Process table body
              const tbody = table.querySelector('tbody');
              if (tbody) {
                richText += '<tbody>';
                Array.from(tbody.querySelectorAll('tr')).forEach(tr => {
                  richText += '<tr>';
                  Array.from(tr.querySelectorAll('td')).forEach(td => {
                    richText += `<td>${td.textContent}</td>`;
                  });
                  richText += '</tr>';
                });
                richText += '</tbody>';
              }
              richText += '</table>';
            }
          } else {
            richText += element.textContent;
          }
          break;
        default:
          richText += element.textContent;
      }
      
      // Add line breaks between elements
      if (index < tempDiv.children.length - 1) {
        richText += '\n';
      }
    });
    
    return richText;
  };

  // Drag and drop logic
  const handleDragStart = (e, group, variable) => {
    e.dataTransfer.setData("text/plain", `{{${group}_${variable.replace(/\s+/g, "_")}}}`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const placeholder = e.dataTransfer.getData("text/plain");
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    quill.insertText(range.index, placeholder);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Place helper functions before the return statement
  const renderFormPreview = () => {
    const regex = /\{\{([^}]+)\}\}/g;
    let html = content;
    const elements = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(html)) !== null) {
      const before = html.slice(lastIndex, match.index);
      if (before) elements.push(<span key={key++} dangerouslySetInnerHTML={{ __html: before }} />);
      const placeholder = match[1];
      if (placeholder.startsWith('input_types_')) {
        // Render input type
        const type = placeholder.replace('input_types_', '');
        let inputEl = null;
        switch (type) {
          case 'text':
          case 'email':
          case 'number':
          case 'url':
          case 'phone':
            inputEl = <input key={key++} type={type === 'phone' ? 'tel' : type} style={{ margin: '0 4px', padding: 4, borderRadius: 4, border: '1px solid #ccc' }} placeholder={type} />;
            break;
          case 'date':
            inputEl = <input key={key++} type="date" style={{ margin: '0 4px', padding: 4, borderRadius: 4, border: '1px solid #ccc' }} />;
            break;
          case 'select':
            inputEl = (
              <select key={key++} style={{ margin: '0 4px', padding: 4, borderRadius: 4, border: '1px solid #ccc' }}>
                <option>Option 1</option>
                <option>Option 2</option>
              </select>
            );
            break;
          case 'checkbox':
            inputEl = <input key={key++} type="checkbox" style={{ margin: '0 8px' }} />;
            break;
          case 'radio':
            inputEl = <input key={key++} type="radio" style={{ margin: '0 8px' }} />;
            break;
          case 'textarea':
            inputEl = <textarea key={key++} rows={2} style={{ margin: '0 4px', borderRadius: 4, border: '1px solid #ccc', verticalAlign: 'middle' }} placeholder="textarea" />;
            break;
          default:
            inputEl = <input key={key++} type="text" style={{ margin: '0 4px', padding: 4, borderRadius: 4, border: '1px solid #ccc' }} placeholder={type} />;
        }
        elements.push(inputEl);
      } else {
        // Render variable as chip
        elements.push(
          <Chip
            key={key++}
            label={`{{${placeholder}}}`}
            color="info"
            variant="filled"
            size="small"
            sx={{ mx: 0.5, fontWeight: 500 }}
          />
        );
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < html.length) {
      elements.push(<span key={key++} dangerouslySetInnerHTML={{ __html: html.slice(lastIndex) }} />);
    }
    return <div style={{ padding: 24 }}>{elements}</div>;
  };

  // PDF Preview: static, with logo and template name at the top, all placeholders as chips
  const renderPdfPreview = () => {
    // Use converted HTML if available, otherwise use rich text content
    const displayContent = convertedHtml || content;
    
    const regex = /\{\{([^}]+)\}\}/g;
    let html = displayContent;
    const elements = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    // PDF-like page container
    elements.push(
      <div key="pdf-header" style={{
        background: '#fff',
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        borderRadius: 8,
        margin: '32px auto',
        padding: '48px 56px',
        maxWidth: 800,
        minHeight: 600,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <img src={fdLogo} alt="Fisher Dugmore Financial Logo" style={{ width: 120, marginBottom: 24 }} />
        <h2 style={{ textTransform: 'uppercase', margin: 0, marginBottom: 32, letterSpacing: 2, fontWeight: 700, fontSize: 32, textAlign: 'center' }}>{(templateName || 'TEMPLATE NAME')}</h2>
        <div style={{ width: '100%', textAlign: 'left' }}>
          {/* Content will be inserted here */}
        </div>
      </div>
    );
    
    // If we have converted HTML, render it directly
    if (convertedHtml) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <style>
            {`
              .contract-form {
                font-family: 'Times New Roman', serif;
                line-height: 1.6;
                color: #333;
              }
              .contract-form h1, .contract-form h2 {
                margin: 20px 0 10px 0;
                font-weight: bold;
                color: #2c3e50;
              }
              .contract-form h1 {
                font-size: 24px;
                text-align: center;
              }
              .contract-form h2 {
                font-size: 20px;
                border-bottom: 2px solid #3498db;
                padding-bottom: 5px;
              }
              .contract-form p {
                margin: 10px 0;
                text-align: justify;
              }
              .contract-form .section-h1, .contract-form .section-h2, .contract-form .section-p {
                margin: 15px 0;
              }
              .contract-form .numbered-list {
                margin: 15px 0;
                padding-left: 20px;
              }
              .contract-form .numbered-list .list-item {
                margin: 8px 0;
                padding: 5px 0;
              }
              .contract-form .unordered-list {
                margin: 15px 0;
                padding-left: 20px;
              }
              .contract-form .unordered-list .list-item {
                margin: 5px 0;
              }
              .contract-form .sub-list {
                margin: 10px 0;
                padding-left: 20px;
              }
              .contract-form .sub-item {
                margin: 5px 0;
                padding: 3px 0;
              }
              .contract-form .table-container {
                margin: 20px 0;
                overflow-x: auto;
              }
              .contract-form .data-table {
                width: 100%;
                border-collapse: collapse;
                border: 1px solid #ddd;
              }
              .contract-form .data-table th,
              .contract-form .data-table td {
                border: 1px solid #ddd;
                padding: 8px 12px;
                text-align: left;
              }
              .contract-form .data-table th {
                background-color: #f8f9fa;
                font-weight: bold;
              }
              .contract-form input,
              .contract-form select,
              .contract-form textarea {
                border: 1px solid #ccc;
                border-radius: 4px;
                padding: 4px 8px;
                font-size: 14px;
                background-color: #fff;
              }
              .contract-form input:focus,
              .contract-form select:focus,
              .contract-form textarea:focus {
                outline: none;
                border-color: #3498db;
                box-shadow: 0 0 5px rgba(52, 152, 219, 0.3);
              }
            `}
          </style>
          <div style={{
            background: '#fff',
            boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
            borderRadius: 8,
            margin: '32px auto',
            padding: '48px 56px',
            maxWidth: 800,
            minHeight: 600,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <img src={fdLogo} alt="Fisher Dugmore Financial Logo" style={{ width: 120, marginBottom: 24 }} />
            <h2 style={{ textTransform: 'uppercase', margin: 0, marginBottom: 32, letterSpacing: 2, fontWeight: 700, fontSize: 32, textAlign: 'center' }}>{(templateName || 'TEMPLATE NAME')}</h2>
            <div 
              style={{ width: '100%', textAlign: 'left' }}
              dangerouslySetInnerHTML={{ __html: convertedHtml }}
            />
          </div>
        </div>
      );
    }
    
    // Content rendering for rich text (original logic)
    const contentElements = [];
    while ((match = regex.exec(html)) !== null) {
      const before = html.slice(lastIndex, match.index);
      if (before) contentElements.push(<span key={key++} dangerouslySetInnerHTML={{ __html: before }} />);
      const placeholder = match[1];
      // Always render as chip in PDF preview
      contentElements.push(
        <Chip
          key={key++}
          label={`{{${placeholder}}}`}
          color="info"
          variant="filled"
          size="small"
          sx={{ mx: 0.5, fontWeight: 500 }}
        />
      );
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < html.length) {
      contentElements.push(<span key={key++} dangerouslySetInnerHTML={{ __html: html.slice(lastIndex) }} />);
    }
    // Insert content into the PDF container
    elements[0] = React.cloneElement(elements[0], {},
      elements[0].props.children[0], // logo
      elements[0].props.children[1], // title
      <div style={{ width: '100%', textAlign: 'left' }}>{contentElements}</div>
    );
    return <div style={{ display: 'flex', justifyContent: 'center' }}>{elements}</div>;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '75vw',
          height: '90vh',
          maxWidth: '75vw',
          maxHeight: '90vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <MDTypography variant="h5">Create New Template (Beta)</MDTypography>
      </DialogTitle>
      <DialogContent sx={{ height: '100%', p: 3 }}>
        <MDBox mb={2}>
          <MDInput
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            required
          />
        </MDBox>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="Template Content" />
          <Tab label="PDF Preview" />
          <Tab label="JSON Input" />
        </Tabs>
        {activeTab === 0 && (
          <MDBox display="flex" gap={4} height="calc(100% - 56px)">
            {/* Chips */}
            <MDBox minWidth={220} height="100%" maxHeight="100%" overflow="auto" pr={2}>
              <MDTypography variant="subtitle2" mb={1}>
                Variables
              </MDTypography>
              {VARIABLE_GROUPS.map(group => (
                <MDBox key={group.label} mb={2}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    {group.label}
                  </MDTypography>
                  <Stack direction="column" spacing={1} mt={1}>
                    {group.options.map(option => (
                      <Chip
                        key={option}
                        label={`{{${group.label}_${option.replace(/\s+/g, "_")}}}`}
                        draggable
                        onDragStart={e => handleDragStart(e, group.label, option)}
                        color={GROUP_COLORS[group.label] || 'info'}
                        variant="filled"
                        sx={{ cursor: "grab", fontWeight: 500 }}
                      />
                    ))}
                  </Stack>
                </MDBox>
              ))}
            </MDBox>
            {/* Rich Text Editor */}
            <MDBox flex={1} height="100%" display="flex" flexDirection="column" onDrop={handleDrop} onDragOver={handleDragOver}>
              <MDTypography variant="subtitle2" mb={1}>
                Template Content
              </MDTypography>
              <MDBox flex={1} minHeight={0}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  style={{ height: '100%', minHeight: 0 }}
                  modules={quillModules}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        )}
        {activeTab === 1 && (
          <MDBox height="calc(100% - 56px)" overflow="auto" bgcolor="#f5f5f5">
            {renderPdfPreview()}
          </MDBox>
        )}
        {activeTab === 2 && (
          <MDBox height="calc(100% - 56px)" display="flex" flexDirection="column" gap={2}>
            <MDBox display="flex" gap={2} height="50%">
              {/* JSON Input Section */}
              <MDBox flex={1} display="flex" flexDirection="column">
                <MDTypography variant="h6" mb={1}>
                  JSON Template Input
                </MDTypography>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder="Paste your JSON template here..."
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }
                  }}
                />
                <MDBox mt={1} display="flex" gap={1}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleJsonConversion}
                    disabled={!jsonInput.trim()}
                    startIcon={<Icon>preview</Icon>}
                  >
                    Convert to HTML
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => setJsonInput(defaultJsonTemplate)}
                    startIcon={<Icon>content_copy</Icon>}
                  >
                    Load Sample
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Conversion Results */}
              <MDBox flex={1} display="flex" flexDirection="column">
                <MDTypography variant="h6" mb={1}>
                  Conversion Results
                </MDTypography>
                {conversionError && (
                  <Card sx={{ border: '2px solid #f44336', backgroundColor: '#ffebee', mb: 2 }}>
                    <MDBox p={2}>
                      <MDBox display="flex" alignItems="center" mb={1}>
                        <Icon sx={{ color: '#f44336', mr: 1 }}>error</Icon>
                        <MDTypography variant="body2" color="error" fontWeight="bold">
                          Conversion Error
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="body2" color="text">
                        {conversionError}
                      </MDTypography>
                    </MDBox>
                  </Card>
                )}
                {convertedHtml && (
                  <Card sx={{ border: '2px solid #4caf50', backgroundColor: '#f1f8e9', flex: 1 }}>
                    <MDBox p={2} height="100%" display="flex" flexDirection="column">
                      <MDBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: '#4caf50', mr: 1 }}>check_circle</Icon>
                        <MDTypography variant="body2" color="success" fontWeight="bold">
                          Successfully Converted!
                        </MDTypography>
                      </MDBox>
                      <MDBox flex={1} overflow="auto">
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                          <pre style={{ 
                            margin: 0, 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-all',
                            fontSize: '0.75rem',
                            fontFamily: 'monospace'
                          }}>
                            {convertedHtml}
                          </pre>
                        </Paper>
                      </MDBox>
                      <MDBox mt={2}>
                        <MDButton
                          variant="gradient"
                          color="success"
                          onClick={handleUseHtmlInEditor}
                          startIcon={<Icon>content_paste</Icon>}
                        >
                          Use in Template Editor
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="info"
                          onClick={() => setActiveTab(1)}
                          startIcon={<Icon>preview</Icon>}
                          sx={{ ml: 1 }}
                        >
                          View PDF Preview
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </Card>
                )}
              </MDBox>
            </MDBox>
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="dark" onClick={handleClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleSave}
          disabled={!templateName.trim() || !content.trim()}
        >
          Save
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

CreateTemplateBetaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialTemplate: PropTypes.object,
};

export default CreateTemplateBetaModal; 